import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import { X, Tag as TagIcon, Plus, Search } from "lucide-react";
import { Device, Tag as DeviceTag } from "../interfaces/EndpointInterfaces";
import axios from "axios";
import { useToast } from "../../../hooks/use-toast";
import { NGROK } from "../../../APIs";
import useUserStore from "../../../services/userStore";
import { myLocalStorage } from "../../StorageHelper";
import { apiClient } from "../../../utils/apiClient";
import { getAllTags } from "../Tags/Api/tagdetailsapi";

interface DeviceTagsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  device: Device | null;
  onSave: (
    deviceId: string,
    existingTags: DeviceTag[],
    newTags: DeviceTag[]
  ) => void;
  onClose: () => void
}

export default function DeviceTags({ open, onOpenChange, device, onSave, onClose }: DeviceTagsProps) {
  const { role, userRole, email, userId } = useUserStore((state) => state.user);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState<DeviceTag[]>([]);
  const [availableTags, setAvailableTags] = useState<DeviceTag[]>([]);
  const [suggestions, setSuggestions] = useState<DeviceTag[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const [selectedTenant, setSelectedTenant] = useState(
    myLocalStorage.getItem("latestTenant")?.tenantName,
  );



  useEffect(() => {
    getTags();

  }, [open]);

  const getTags = async () => {
    if (selectedTenant) {
      let response = await getAllTags(selectedTenant, 0, "");
      const sorted = [...response.content];
      setAvailableTags(sorted);
    }

  }

  // Initialize device tags
  useEffect(() => {
    if (device && open) {
      setSelectedTags(device.tags || []);
      setSearchTerm("");
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [device, open]);

  // Filter suggestions based on search
  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = availableTags.filter(
        tag =>
          tag.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !selectedTags.some(t => t.name === tag.name)
      );
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchTerm, selectedTags, availableTags]);

  const handleAddTag = (tag: DeviceTag) => {
    if (!selectedTags.some(t => t.name === tag.name)) {
      setSelectedTags(prev => [...prev, tag]);
      setSearchTerm("");
      setShowSuggestions(false);
      toast({ title: "Tag Added", description: `"${tag.name}" tag has been added.` });
    }
  };

  const handleRemoveTag = (tag: DeviceTag) => {
    setSelectedTags(prev => prev.filter(t => t.name !== tag.name));
    toast({ title: "Tag Removed", description: `"${tag.name}" tag has been removed.` });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (searchTerm.trim()) {
        const existing = availableTags.find(tag => tag.name.toLowerCase() === searchTerm.toLowerCase());
        handleAddTag(existing || { id: 0, name: searchTerm, tenantName: selectedTenant, description: searchTerm, createdBy: email, updatedBy: email, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
      }
    }
    if (e.key === "Escape") setShowSuggestions(false);
  };

  const handleSave = () => {
    if (device) {

      const previousTags = device.tags || [];

      const existingTags = selectedTags.filter(tag =>
        previousTags.some(prev => prev.name === tag.name)
      );

      const newTags = selectedTags.filter(tag =>
        !previousTags.some(prev => prev.name === tag.name)
      );

      onSave(device.id, existingTags, newTags);
      onOpenChange(false);
      toast({ title: "Tags Updated", description: `Device "${device.name}" tags updated successfully.` });
    }
  };

  const handleCancel = () => {
    setSelectedTags(device?.tags || []);
    setSearchTerm("");
    onOpenChange(false);
    onClose();
  };

  const hasChanges = device && JSON.stringify(selectedTags.map(t => t.name).sort()) !== JSON.stringify((device.tags || []).map(t => t.name).sort());
  const isAdmin = userRole === "whiteswan-admin";
  const isWsAdmin = email?.startsWith("wsadmin");
  const hasElevatedTagPrivileges = isAdmin || isWsAdmin;

  if (!device) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
            <TagIcon className="w-5 h-5 text-blue-600" />
            Configure Tags - {device.name}
          </DialogTitle>
          <DialogDescription className="text-slate-600 dark:text-slate-400">
            Manage tags for this device. Search existing tags or create new ones.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="relative space-y-2">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Search or Create Tags</label>
            <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-slate-400" />
            <Input
              ref={inputRef}
              placeholder="Type to search or add..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyPress}
              onFocus={() => setShowSuggestions(suggestions.length > 0)}
              className="pl-10"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border rounded-md shadow-lg dark:bg-slate-800 border-slate-200 dark:border-slate-700 max-h-48">
                {suggestions.slice(0, 8).map(tag => (
                  <button
                    key={tag.id}
                    className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50 dark:hover:bg-slate-700"
                    onClick={() => handleAddTag(tag)}
                  >
                    <Plus className="w-3 h-3 text-green-500" />
                    {tag.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected Tags */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Assigned Tags ({selectedTags.length})
              </label>
              {selectedTags.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (hasElevatedTagPrivileges) {
                      setSelectedTags([]);
                      toast({ title: "All Tags Removed", description: "All tags have been cleared." });
                    } else {
                      setSelectedTags(prev => prev.filter(tag => tag.createdBy !== email));
                      toast({ title: "Tags Removed", description: "Only your created tags were removed." });
                    }
                  }}
                  className="h-6 px-2 text-xs text-red-600 hover:text-red-700"
                >
                  Clear All
                </Button>
              )}
            </div>
            <div className="min-h-[120px] p-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50/50 dark:bg-slate-800/50">
              {selectedTags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedTags.map(tag => {
                    const isCreator = tag?.createdBy === email;
                    const canEditTags = hasElevatedTagPrivileges || isCreator;

                    return (
                      <Badge
                        key={tag.id || tag.name}
                        variant="secondary"
                        className="flex items-center gap-1 px-3 py-1 text-sm text-blue-800 bg-blue-100 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800"
                      >
                        {tag.name}
                        <button disabled={!canEditTags} onClick={() => handleRemoveTag(tag)} className="ml-1 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    )
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400 dark:text-slate-500">
                  <div className="text-center">
                    <TagIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No tags assigned</p>
                    <p className="text-xs">Search above to add tags to this device</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSave} disabled={!hasChanges} className="text-white bg-blue-600 hover:bg-blue-700">
            {hasChanges ? `Save Changes (${selectedTags.length})` : `No Changes (${selectedTags.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
